/**
 * useAssessmentRequests Hook
 * TanStack Query hooks for investor-side assessment request management.
 *
 * Endpoints:
 * - GET    /api/investor/assessment-requests         — list all requests
 * - POST   /api/investor/assessment-requests         — create new request
 * - DELETE /api/investor/assessment-requests/:id     — cancel request
 * - POST   /api/investor/assessment-requests/:id/resend  — resend invitation
 * - GET    /api/investor/assessment-requests/:id     — single request + milestone detail
 * - POST   /api/investor/assessment-requests/:id/invest  — convert deal → portfolio
 * - POST   /api/investor/assessment-requests/:id/archive — archive a decided deal
 * - POST   /api/investor/assessment-requests/:id/extend  — extend the expiry +7d
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import type {
  RequestUrgency,
  InvestorAssessmentRequest,
  InvestorAssessmentRequestDetail,
} from "@app/types";

/**
 * Investor-facing assessment request shape. Canonical definition lives in
 * `@app/types` as {@link InvestorAssessmentRequest} (Hard Rule 11); re-exported
 * here under the historical name for existing consumers (e.g. PipelinePage).
 */
export type { InvestorAssessmentRequest as AssessmentRequest };
export type { InvestorAssessmentRequestDetail as AssessmentRequestDetail };

const REQUESTS_QUERY_KEY = "investor-assessment-requests";
const REQUEST_DETAIL_QUERY_KEY = "investor-assessment-request-detail";
const PORTFOLIO_QUERY_KEY = "investor-portfolio";

/**
 * Extract a user-facing error message from an apiClient error response.
 * Maps known HTTP status codes to specific messages; falls back to `defaultMessage`.
 */
function toApiError(
  error: unknown,
  response: Response | undefined,
  defaultMessage: string,
  statusMessages: Record<number, string> = {},
): Error {
  const status = response?.status;
  if (status && statusMessages[status]) {
    return new Error(statusMessages[status]);
  }
  const bodyMessage = (error as Record<string, unknown>)?.message;
  if (status && typeof bodyMessage === "string") {
    return new Error(bodyMessage);
  }
  return new Error(defaultMessage);
}

interface CreateRequestPayload {
  companyName: string;
  ctoEmail: string;
  dealContext?: string;
  urgency?: RequestUrgency;
}

/**
 * Response contract for a successful create-assessment-request call
 * (POST /investor/assessment-requests). The server returns the newly created
 * request (its `id` carries the confirmed scope in the pipeline flow) plus the
 * one-time invitation token. Typed here so callers do not have to cast the
 * mutation result; `request.id` is still guarded at call sites because a
 * malformed/partial success body cannot be ruled out by the type alone.
 */
export interface CreateAssessmentRequestResult {
  request: { id: string };
  rawToken?: string;
}

/**
 * Fetch all assessment requests for the investor's fund.
 */
export function useAssessmentRequests() {
  const { isAuthenticated } = useAuth();
  const { currentTenant } = useTenant();

  const { data, isLoading, error } = useQuery<
    InvestorAssessmentRequest[],
    Error
  >({
    queryKey: [REQUESTS_QUERY_KEY, currentTenant?.id],
    queryFn: async (): Promise<InvestorAssessmentRequest[]> => {
      const { data, error: apiError } = await apiClient.GET(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "/investor/assessment-requests" as any,
      );

      if (apiError) {
        throw new Error("Failed to fetch assessment requests");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any).requests ?? [];
    },
    enabled: isAuthenticated && !!currentTenant,
    staleTime: 30 * 1000,
  });

  return {
    requests: data ?? [],
    isLoading,
    error,
  };
}

/**
 * Fetch a single assessment request's in-flight detail (milestone + the real
 * per-milestone timestamps) for the `/pipeline/:id` deal status page. The query
 * key includes the request id so each deal caches independently and
 * {@link useExtendRequest} can target this exact entry for invalidation.
 *
 * @param requestId - The assessment request id (from the route param); the query
 *   is disabled when falsy (or when unauthenticated / no selected tenant).
 * @returns A TanStack query of {@link InvestorAssessmentRequestDetail}.
 * @throws Error (surfaced via the query's `error`) with a user-facing message —
 *   "Deal not found" on 404, else a generic load failure — when the fetch fails.
 */
export function useAssessmentRequestDetail(requestId: string | undefined) {
  const { isAuthenticated } = useAuth();
  const { currentTenant } = useTenant();

  return useQuery<InvestorAssessmentRequestDetail, Error>({
    queryKey: [REQUEST_DETAIL_QUERY_KEY, requestId, currentTenant?.id],
    queryFn: async (): Promise<InvestorAssessmentRequestDetail> => {
      const { data, error, response } = await apiClient.GET(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}` as any,
      );

      if (error) {
        throw toApiError(error, response, "Failed to load deal status", {
          404: "Deal not found",
        });
      }

      return data as InvestorAssessmentRequestDetail;
    },
    enabled: isAuthenticated && !!currentTenant && !!requestId,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a new assessment request.
 */
export function useCreateAssessmentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateRequestPayload,
    ): Promise<CreateAssessmentRequestResult> => {
      const { data, error, response } = await apiClient.POST(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "/investor/assessment-requests" as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { body: payload as any },
      );

      if (error) {
        throw toApiError(
          error,
          response,
          "Failed to create assessment request",
          {
            409: "An active assessment request already exists for this email",
            403: "Not authorized",
          },
        );
      }

      return data as CreateAssessmentRequestResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["investor-portfolio"] });
    },
  });
}

/**
 * Cancel an assessment request.
 */
export function useCancelAssessmentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error, response } = await apiClient.DELETE(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}` as any,
      );

      if (error) {
        throw toApiError(
          error,
          response,
          "Failed to cancel assessment request",
          {
            404: "Assessment request not found",
            409: "Cannot cancel this request",
          },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
    },
  });
}

/** Optional outcome metadata recorded when archiving a deal. */
interface ArchiveRequestPayload {
  /** The assessment request / deal being archived. */
  requestId: string;
  /** Optional free-text outcome decision (e.g. "passed — valuation"). */
  decision?: string;
  /** Optional free-text notes. */
  notes?: string;
}

/**
 * Convert a completed pipeline deal into a portfolio company
 * (POST /invest). The backend performs the transition atomically
 * (flips the portfolio link to PORTFOLIO, stamps `investedAt`, cancels
 * reminder jobs), so on success we invalidate BOTH the assessment-request
 * list and the portfolio query — the company must appear in the Portfolio
 * and disappear from the Pipeline in the same render (atomic-transition
 * requirement, sv-0f2).
 *
 * @returns A TanStack mutation whose `mutate` accepts the request id.
 * @throws Error with a user-facing message on 404 (not found) or 409
 *   (already invested), else a generic failure message.
 */
export function useInvestRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error, response } = await apiClient.POST(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}/invest` as any,
        {},
      );

      if (error) {
        throw toApiError(error, response, "Failed to mark deal invested", {
          404: "Assessment request not found",
          409: "This deal has already been marked invested",
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PORTFOLIO_QUERY_KEY] });
    },
  });
}

/**
 * Archive a decided pipeline deal (POST /archive) with optional free-text
 * `decision`/`notes`. Archiving removes the deal from the active pipeline but
 * leaves the DEAL_ACCESS portfolio link intact. On success both the
 * assessment-request list and the portfolio query are invalidated so the two
 * spaces stay consistent.
 *
 * @returns A TanStack mutation whose `mutate` accepts {@link ArchiveRequestPayload}.
 * @throws Error with a user-facing message on 404 (not found), else a generic
 *   failure message.
 */
export function useArchiveRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      decision,
      notes,
    }: ArchiveRequestPayload) => {
      const { data, error, response } = await apiClient.POST(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}/archive` as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { body: { decision, notes } as any },
      );

      if (error) {
        throw toApiError(error, response, "Failed to archive deal", {
          404: "Assessment request not found",
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PORTFOLIO_QUERY_KEY] });
    },
  });
}

/**
 * Resend an assessment request invitation.
 */
export function useResendAssessmentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error, response } = await apiClient.POST(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}/resend` as any,
        {},
      );

      if (error) {
        throw toApiError(error, response, "Failed to resend invitation", {
          404: "Assessment request not found",
          409: "Cannot resend this request",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
    },
  });
}

/**
 * Extend an in-flight deal's expiry by 7 days (POST /extend). The backend
 * reschedules the expiry job and stamps the new deadline. On success both the
 * request list and this deal's detail query are invalidated so the refreshed
 * countdown lands in the same render on the pipeline and the status page.
 *
 * @returns A TanStack mutation whose `mutate` accepts the request id.
 * @throws Error with a user-facing message on 404 (not found) or 409 (the deal
 *   is terminal / archived / already invested and can't be extended), else a
 *   generic failure message.
 */
export function useExtendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error, response } = await apiClient.POST(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/investor/assessment-requests/${requestId}/extend` as any,
        {},
      );

      if (error) {
        throw toApiError(error, response, "Failed to extend the deadline", {
          404: "Assessment request not found",
          409: "This deal can no longer be extended",
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REQUEST_DETAIL_QUERY_KEY] });
    },
  });
}
